
import { Student, TrialExamResult } from '../types';
import { API_URL, apiFetch } from './api';

// Fallback static data import (used if API fails)
import { TOPIC_HISTORY_DATA as FALLBACK_DATA, TopicHistoryItem } from '../constants/topicHistoryData';

export interface Recommendation {
    id: string;
    topic: string;
    lesson: string;
    examType: 'TYT' | 'AYT';
    priority: 1 | 2 | 3;
    reason: string;
    mistakeCount: number;
    annualAverage: number;
    history: { [year: number]: number };
    consistentMistake: boolean; // 4+ denemede sürekli yanlış mı?
    examsMistakenIn: number; // Kaç farklı denemede yanlış yapılmış?
}

// Cache for topic history data
let cachedTopicHistoryData: TopicHistoryItem[] | null = null;
let cacheTimestamp: number = 0;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/**
 * Fetch topic history data from API, with fallback to static data
 */
export const fetchTopicHistoryData = async (): Promise<TopicHistoryItem[]> => {
    // Check cache first
    const now = Date.now();
    if (cachedTopicHistoryData && (now - cacheTimestamp) < CACHE_DURATION) {
        return cachedTopicHistoryData;
    }

    try {
        const response = await apiFetch(`${API_URL}/api/topic-history`);
        if (!response.ok) {
            throw new Error('API response not ok');
        }

        const data = await response.json();

        if (data && data.length > 0) {
            // Transform API data to match TopicHistoryItem interface
            const transformed: TopicHistoryItem[] = data.map((item: any) => ({
                examType: item.examType,
                lesson: item.lesson,
                topic: item.topic,
                history: typeof item.history === 'object' ? item.history : {},
                totalLast7Years: item.totalLast7Years || 0,
                average: item.average || 0,
                importanceScore: item.importanceScore || 0
            }));

            // Update cache
            cachedTopicHistoryData = transformed;
            cacheTimestamp = now;

            console.log(`✅ Loaded ${transformed.length} topics from API`);
            return transformed;
        }
    } catch (error) {
        console.warn('⚠️ Failed to fetch topic history from API, using fallback:', error);
    }

    // Fallback to static data
    console.log(`📦 Using fallback static data (${FALLBACK_DATA.length} topics)`);
    return FALLBACK_DATA;
};

/**
 * Synchronous version that uses cached data or fallback
 * Use this when you can't use async (e.g., in component render)
 */
export const getTopicHistoryDataSync = (): TopicHistoryItem[] => {
    if (cachedTopicHistoryData && cachedTopicHistoryData.length > 0) {
        return cachedTopicHistoryData;
    }
    return FALLBACK_DATA;
};

/**
 * Pre-load topic history data (call this on app initialization)
 */
export const preloadTopicHistoryData = async (): Promise<void> => {
    await fetchTopicHistoryData();
};

export const analyzeStudentExams = (student: Student): Recommendation[] => {
    if (!student.trialExams || student.trialExams.length === 0) return [];

    // Use cached data or fallback
    const topicHistoryData = getTopicHistoryDataSync();

    // 1. Aggregate Mistakes
    const mistakeMap: Record<string, number> = {};
    // Track how many distinct exams each topic was wrong in
    const examCountMap: Record<string, Set<string>> = {};

    student.trialExams.forEach(exam => {
        if (!exam.topicAnalysis) return;
        exam.topicAnalysis.forEach(analysis => {
            if (analysis.incorrect > 0 || analysis.empty > 0) {
                // Key format: TYT-Matematik-Polinomlar
                const key = `${exam.examType}-${analysis.subject}-${analysis.topic}`;
                mistakeMap[key] = (mistakeMap[key] || 0) + analysis.incorrect + (analysis.empty * 0.5);
                if (!examCountMap[key]) examCountMap[key] = new Set();
                examCountMap[key].add(String(exam.id));
            }
        });
    });

    const recommendations: Recommendation[] = [];

    // 2. Correlate with History Data
    topicHistoryData.forEach(historyItem => {
        // Try strict match first
        const key = `${historyItem.examType}-${historyItem.lesson}-${historyItem.topic}`;
        let mistakes = mistakeMap[key] || 0;

        // Fallback: fuzzy match if 0 mistakes found strictly
        if (mistakes === 0) {
            const potentialKey = Object.keys(mistakeMap).find(k =>
                k.includes(historyItem.lesson) && k.endsWith(historyItem.topic)
            );
            if (potentialKey) {
                mistakes = mistakeMap[potentialKey];
            }
        }

        // Only recommend if the student has actually struggled with it
        if (mistakes > 0) {
            let priority: 1 | 2 | 3 = 3;

            // Check if this is a "5-question subject" that needs proportional scaling
            const fiveQuestionSubjects = ['Felsefe', 'Din Kültürü', 'Tarih', 'Coğrafya', 'Coğrafya-1', 'Tarih-1'];
            const isFiveQuestionSubject = fiveQuestionSubjects.includes(historyItem.lesson);

            const recentAvg = ((historyItem.history[2025] || 0) + (historyItem.history[2024] || 0)) / 2;

            if (isFiveQuestionSubject) {
                // For 5-question subjects, use proportional thresholds:
                // Priority 1: >= 1.0 avg (20%+ of subject) OR recent surge >= 1.5
                // Priority 2: >= 0.5 avg (10%+ of subject)
                // Priority 3: < 0.5 avg
                if (historyItem.average >= 1.0 || recentAvg >= 1.5) {
                    priority = 1;
                } else if (historyItem.average >= 0.5) {
                    priority = 2;
                } else {
                    priority = 3;
                }
            } else {
                // For high-question subjects (Türkçe, Matematik, etc.):
                // Priority 1: >= 3.0 avg OR Recent Surge (>= 3 in last 2 years)
                // Priority 2: >= 1.5 avg
                // Priority 3: < 1.5 avg
                if (historyItem.average >= 3.0 || recentAvg >= 3) {
                    priority = 1;
                } else if (historyItem.average >= 1.5) {
                    priority = 2;
                } else {
                    priority = 3;
                }
            }

            const examsMistakenIn = examCountMap[key]?.size ||
                Object.keys(examCountMap).filter(k =>
                    k.includes(historyItem.lesson) && k.endsWith(historyItem.topic)
                ).reduce((max, k) => Math.max(max, examCountMap[k]?.size || 0), 0);

            recommendations.push({
                id: key,
                topic: historyItem.topic,
                lesson: historyItem.lesson,
                examType: historyItem.examType,
                priority,
                reason: generateReason(historyItem, mistakes, priority),
                mistakeCount: mistakes,
                annualAverage: historyItem.average,
                history: historyItem.history,
                consistentMistake: examsMistakenIn >= 4,
                examsMistakenIn
            });
        }
    });

    // 3. Sort: Priority (asc), then Average (desc), then Mistake Count (desc)
    return recommendations.sort((a, b) => {
        if (a.priority !== b.priority) return a.priority - b.priority;
        if (b.annualAverage !== a.annualAverage) return b.annualAverage - a.annualAverage;
        return b.mistakeCount - a.mistakeCount;
    });
};

const generateReason = (item: TopicHistoryItem, mistakes: number, priority: number): string => {
    const years = [2025, 2024, 2023];
    const recentTrend = years.map(y => `${y}: ${item.history[y] || 0}`).join(', ');

    if (priority === 1) {
        return `🔥 Çok Kritik! Her yıl ortalama ${item.average} soru çıkıyor. (${recentTrend})`;
    }
    if (priority === 2) {
        return `⚠️ Önemli Konu. Ortalama ${item.average} soru. Eksiğini kapatmalısın.`;
    }
    return `Tekrar Önerisi. Bu konuda hataların var (${mistakes} hata).`;
};
